// BUILD-046 J1a (Customer / Job association): the model of the searchable "Customer / Job" field of the Assign task dialog.
//
// The field sits in the Customer / Job + Task due date row of Assign task (cycle C; it was directly below the Task title row before). A person types at least two characters; after a short pause the page asks the limited job directory
// (GET /work/jobs) and shows at most 20 active jobs. One job may be chosen; it is held as `{ jobId, label }` and the request carries the STABLE id only
// (the server checks it against the directory and stores the name itself). The field is optional and never blocks the task.
//
// What this module decides (the component draws it, the same split as the people combobox):
//   - when a search is sent (two characters or more, after the pause the component applies) and when it is not (nothing, or "type at least 2");
//   - which answer counts: every request has a number, and an answer for any other number is dropped, so a slow old answer can never replace a newer
//     one (the Caller-epoch check is the API client's);
//   - the three honest states of a lookup: results, "no jobs match" (only after the directory ANSWERED with nothing) and a failure with Retry. A failure
//     is NEVER shown as "no matches", and it never discards what the person typed;
//   - the keys: the same combobox rules as the people field (peopleComboboxModel.decideKey), single choice, no "remove last chip" on Backspace;
//   - the row wording: the job name first, then the customer and the code when the directory sent them and they add something.
//
// Pure module: no React, no DOM, no I/O, no clock. Erasable TypeScript; type-only import from the API module.

import type { WorkJobPick, WorkJobSearch } from '../../api/workShared';
import { closeList, decideKey, optionId } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import { stripTestMarker } from './displayNames';

/** Fewer characters than this ask the directory nothing (no browse-all). The server enforces the same number. */
export const JOB_MIN_CHARS = 2;
/** The pause the component applies after the last keystroke before it asks (a burst of typing sends one request). */
export const JOB_SEARCH_DELAY_MS = 250;
/** The most rows shown; the server never sends more. */
export const JOB_RESULT_CAP = 20;
/** The longest search text sent; the server clips at the same length. */
export const JOB_QUERY_MAX = 80;
/** The same id shape the server accepts. */
const JOB_ID_SHAPE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,200}$/;

/** The job a person chose: the stable id and the name shown in the chip. Nothing else is kept or sent. */
export interface JobChoice {
  jobId: string;
  label: string;
}

export function isJobChoice(value: unknown): value is JobChoice {
  if (value === null || typeof value !== 'object') return false;
  const v = value as { jobId?: unknown; label?: unknown };
  return typeof v.jobId === 'string' && JOB_ID_SHAPE.test(v.jobId) && typeof v.label === 'string' && v.label.trim() !== '';
}

/** The search text: white space collapsed, trimmed, at most 80 characters. */
export function normalizeJobQuery(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const text = raw.replace(/\s+/g, ' ').trim();
  return text.length > JOB_QUERY_MAX ? text.slice(0, JOB_QUERY_MAX).trim() : text;
}

export type SearchPlan = { kind: 'idle' } | { kind: 'too-short' } | { kind: 'search'; q: string };

/** What typing this text should do: nothing (empty), wait for more characters, or ask the directory. */
export function planJobSearch(raw: unknown): SearchPlan {
  const q = normalizeJobQuery(raw);
  if (q === '') return { kind: 'idle' };
  if (q.length < JOB_MIN_CHARS) return { kind: 'too-short' };
  return { kind: 'search', q };
}

// ───────────────────────── the lookup's failure wording ─────────────────────────

export type JobFailureType = 'unavailable' | 'not-allowed' | 'sign-in' | 'other';

export interface JobSearchFailure {
  type: JobFailureType;
  headline: string;
  detail: string;
  /** Whether Retry can help: not for a refusal of the right to search. */
  canRetry: boolean;
}

interface ThrownLike {
  kind?: unknown;
  status?: unknown;
  code?: unknown;
}

/**
 * What a failed lookup says. A directory that cannot be asked, a timeout and a lost connection read as "can't be checked right now" (not as "no
 * matches"); the person can try again, or leave the field blank and assign the task without a job. Nothing typed or chosen is lost.
 */
export function describeJobFailure(thrown: unknown): JobSearchFailure {
  const t: ThrownLike = thrown !== null && typeof thrown === 'object' ? (thrown as ThrownLike) : {};
  const status = typeof t.status === 'number' ? t.status : 0;
  if (status === 401) return { type: 'sign-in', headline: "The job list can't be checked.", detail: 'Your identity could not be verified.', canRetry: true };
  if (status === 403) return { type: 'not-allowed', headline: "You can't search jobs here.", detail: 'You can still assign the task without a job.', canRetry: false };
  if (t.kind === 'timeout' || t.kind === 'network' || status >= 500 || t.code === 'STORE_UNAVAILABLE') {
    return { type: 'unavailable', headline: "The job list can't be checked right now.", detail: 'Try again, or leave Customer / Job blank and assign the task without it.', canRetry: true };
  }
  return { type: 'other', headline: "The job search didn't work.", detail: 'Try again, or leave Customer / Job blank and assign the task without it.', canRetry: true };
}

// ───────────────────────── the search state ─────────────────────────

export type SearchStatus = 'idle' | 'too-short' | 'loading' | 'ready' | 'failed';

export interface JobSearchState {
  /** The number of the latest request (or reset); an answer carrying any other number is dropped. */
  seq: number;
  /** The text the status and rows belong to. */
  q: string;
  status: SearchStatus;
  jobs: WorkJobPick[];
  /** The directory held back results beyond the cap. */
  more: boolean;
  failure: JobSearchFailure | null;
}

export function initialJobSearch(): JobSearchState {
  return { seq: 0, q: '', status: 'idle', jobs: [], more: false, failure: null };
}

/**
 * The text changed (after the pause) or Retry was pressed. Returns the new state and, when the directory must be asked, the request to send. Any answer
 * still in flight for an earlier request is dropped from now on, because the number moved on.
 */
export function searchRequested(state: JobSearchState, raw: unknown): { state: JobSearchState; request: { seq: number; q: string } | null } {
  const plan = planJobSearch(raw);
  const seq = state.seq + 1;
  if (plan.kind === 'idle') return { state: { seq, q: '', status: 'idle', jobs: [], more: false, failure: null }, request: null };
  if (plan.kind === 'too-short') return { state: { seq, q: normalizeJobQuery(raw), status: 'too-short', jobs: [], more: false, failure: null }, request: null };
  return { state: { seq, q: plan.q, status: 'loading', jobs: [], more: false, failure: null }, request: { seq, q: plan.q } };
}

function usable(job: unknown): job is WorkJobPick {
  if (job === null || typeof job !== 'object') return false;
  const j = job as { jobId?: unknown; label?: unknown };
  return typeof j.jobId === 'string' && JOB_ID_SHAPE.test(j.jobId) && typeof j.label === 'string' && j.label.trim() !== '';
}

/** The directory answered for request `seq`. Applies only if that is still the latest request. Rows that are not usable are skipped, each job shows once, at most 20. */
export function searchSucceeded(state: JobSearchState, seq: number, result: WorkJobSearch): JobSearchState {
  if (seq !== state.seq || state.status !== 'loading' || result === null || typeof result !== 'object' || !Array.isArray(result.jobs)) return state;
  const seen = new Set<string>();
  const jobs: WorkJobPick[] = [];
  let skippedForCap = false;
  for (const job of result.jobs) {
    if (!usable(job) || seen.has(job.jobId)) continue;
    if (jobs.length >= JOB_RESULT_CAP) {
      skippedForCap = true;
      continue;
    }
    seen.add(job.jobId);
    jobs.push(job);
  }
  return { ...state, status: 'ready', jobs, more: result.more === true || skippedForCap, failure: null };
}

/** The lookup for request `seq` failed. A stale failure (an older request, or the Caller changed) is dropped. What was typed stays; the rows go. */
export function searchFailed(state: JobSearchState, seq: number, thrown: unknown): JobSearchState {
  if (seq !== state.seq || state.status !== 'loading') return state;
  if (thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale') return state;
  return { ...state, status: 'failed', jobs: [], more: false, failure: describeJobFailure(thrown) };
}

// ───────────────────────── what the list shows ─────────────────────────

export interface JobRow {
  jobId: string;
  label: string;
  /** The customer and the code, when the directory sent them and they add something to the name; otherwise null. */
  detail: string | null;
}

function sameText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** One row of the list: the name, then "customer - code" for whatever adds something to it. */
export function rowOf(job: WorkJobPick): JobRow {
  const parts: string[] = [];
  // The row is SHOWN without the "(FICTIONAL)" test marker (display only: the id is what is sent, and the directory's own name is what the server stores).
  const label = stripTestMarker(job.label);
  const customer = stripTestMarker(job.customer);
  const code = stripTestMarker(job.code);
  if (customer !== '' && !sameText(customer, label) && !label.toLowerCase().includes(customer.toLowerCase())) parts.push(customer);
  if (code !== '' && !sameText(code, label) && !label.toLowerCase().includes(code.toLowerCase())) parts.push(code);
  return { jobId: job.jobId, label, detail: parts.length > 0 ? parts.join(' \u00b7 ') : null };
}

export const JOB_MORE_NOTE = 'Showing the first 20 matches. Type more of the name to narrow the list.';
export const JOB_TOO_SHORT_NOTE = 'Type at least 2 characters to search.';

export interface JobSearchView {
  status: SearchStatus;
  rows: JobRow[];
  /** The line under the rows when the directory held more back; null otherwise. */
  moreNote: string | null;
  /** "No jobs match ..." only when the directory ANSWERED with nothing; null otherwise (a failure is never an empty list). */
  emptyText: string | null;
  /** "Type at least 2 characters" while the text is too short; null otherwise. */
  hint: string | null;
  failure: JobSearchFailure | null;
  /** The polite status line for assistive technology; '' while nothing is happening. */
  announcement: string;
}

export function describeJobSearch(state: JobSearchState): JobSearchView {
  const rows = state.status === 'ready' ? state.jobs.map(rowOf) : [];
  const empty = state.status === 'ready' && rows.length === 0;
  let announcement = '';
  if (state.status === 'loading') announcement = 'Searching jobs\u2026';
  else if (state.status === 'ready') announcement = rows.length === 0 ? 'No jobs match.' : `${rows.length} ${rows.length === 1 ? 'job' : 'jobs'} found. Use the arrow keys, then Enter to choose.`;
  else if (state.status === 'failed' && state.failure !== null) announcement = `${state.failure.headline} ${state.failure.detail}`;
  else if (state.status === 'too-short') announcement = JOB_TOO_SHORT_NOTE;
  return {
    status: state.status,
    rows,
    moreNote: state.status === 'ready' && state.more && rows.length > 0 ? JOB_MORE_NOTE : null,
    emptyText: empty ? `No jobs match "${state.q}".` : null,
    hint: state.status === 'too-short' ? JOB_TOO_SHORT_NOTE : null,
    failure: state.status === 'failed' ? state.failure : null,
    announcement,
  };
}

// ───────────────────────── keys ─────────────────────────

export interface JobKeyDecision {
  /** The key was handled here: the caller prevents the browser default. */
  handled: boolean;
  /** Stop the key from reaching the dialog (the Escape that closed the list). */
  stop: boolean;
  state: ComboState;
  /** The job id Enter chose, or null. */
  choose: string | null;
}

/**
 * What a key does: the people combobox rules for a single choice. ArrowDown and ArrowUp move (wrapping), Home and End jump, Enter chooses the active row,
 * Escape closes the list (with it already closed the key is left to the dialog), and Tab only closes the list and moves on: it never chooses a job.
 * Backspace never removes a chosen job here (the chip has its own Remove button).
 */
export function decideJobKey(key: string, combo: ComboState, rows: readonly { jobId: string }[]): JobKeyDecision {
  const decision = decideKey(key, combo, rows.map((r) => ({ employeeId: r.jobId })), 0);
  return { handled: decision.handled, stop: decision.stop, state: decision.state, choose: decision.choose };
}

/** The list closes with the typed text kept (a click outside, leaving the field). */
export function closeJobList(combo: ComboState): ComboState {
  return closeList(combo);
}

/** A stable, id-safe element id for one job row. */
export function jobOptionId(baseId: string, jobId: string): string {
  return optionId(baseId, jobId);
}

// ───────────────────────── the chosen job ─────────────────────────

export interface JobChipView {
  label: string;
  removeLabel: string;
}

export function describeJobChip(job: JobChoice): JobChipView {
  const label = stripTestMarker(job.label);
  return { label, removeLabel: `Remove job ${label}` };
}
